import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { ChevronRight, ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

// Slice #7b — Compliance traffic-light surface (offline-resilient).
//
// One RAG card per portfolio over the live `mobile_compliance_traffic_light(_portfolios)` RPC. The read
// routes through `useCachedQuery` so last-known-good is written through to the persistent cache, a
// synchronous cached placeholder paints before/without a network round-trip, and the page never blanks
// offline — it shows the saved basket plus a stale/offline hint. The RPC self-gates on surface access
// (raises 42501); the queryFn re-throws the Postgrest error verbatim (preserving `.code`) so React Query
// keeps the cached placeholder AND we can surface a graceful "no access" state. RAG is conveyed by colour
// AND text/icon (never colour alone) for accessibility.
//
// CLUSTER-3 (research-visibility): each card is now TAPPABLE through to a per-portfolio findings
// drill-down (/partner/m/compliance/:portfolio, MobileComplianceFindings) so the RAG colour is no longer a
// dead end — the user can see WHICH findings drive it and acknowledge them.

type Rag = 'red' | 'amber' | 'green';
type Severity = 'critical' | 'high' | 'medium' | 'low';

interface ComplianceRow {
  portfolio: string;
  rag: Rag;
  open_count: number;
  worst_severity: Severity | null;
  worst_title: string | null;
}

// Postgres SQLSTATE for insufficient_privilege — the RPC raises this without cio/pm/analyst surface
// access. Supabase surfaces it on the error object's `.code`.
const NO_ACCESS_CODE = '42501';

// No per-user portfolio source exists in the DB (recon §5), so request the full default basket.
const PORTFOLIO_BASKET = ['P1', 'P2', 'P3', 'P4', 'P5', 'R1', 'R2', 'R3', 'R4', 'R5'] as const;

// Read a Postgrest-style SQLSTATE off a thrown error without widening the typed `Error` surface.
function errorCode(err: unknown): string | undefined {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = (err as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

async function fetchComplianceTrafficLight(): Promise<ComplianceRow[]> {
  // RPC is stale-typed (types not regenerated this session) — cast the rpc accessor.
  const { data, error } = await (supabase.rpc as any)('mobile_compliance_traffic_light', {
    _portfolios: [...PORTFOLIO_BASKET],
  });
  // MUST throw on error so useCachedQuery keeps the cached placeholder (offline) and so the 42501
  // no-access state can be surfaced. Throw the Postgrest error itself to preserve `.code`.
  if (error) throw error;
  return (data ?? []) as ComplianceRow[];
}

const RAG_ORDER: Record<Rag, number> = { red: 0, amber: 1, green: 2 };

interface RagStyle {
  dot: string;
  badge: string;
  label: string;
  Icon: typeof ShieldAlert;
  iconClass: string;
}

const RAG_STYLES: Record<Rag, RagStyle> = {
  red: {
    dot: 'bg-red-500',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    label: 'Red',
    Icon: ShieldAlert,
    iconClass: 'text-red-600 dark:text-red-400',
  },
  amber: {
    dot: 'bg-amber-500',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    label: 'Amber',
    Icon: ShieldQuestion,
    iconClass: 'text-amber-600 dark:text-amber-400',
  },
  green: {
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    label: 'Green',
    Icon: ShieldCheck,
    iconClass: 'text-green-600 dark:text-green-400',
  },
};

function ragSeverityLabel(rag: Rag): string {
  switch (rag) {
    case 'red':
      return 'action required';
    case 'amber':
      return 'watch';
    case 'green':
      return 'clear';
  }
}

export default function MobileComplianceTrafficLight() {
  const online = useIsOnline();

  const feed = useCachedQuery<ComplianceRow[]>({
    queryKey: ['mobile-compliance'],
    cacheKey: 'mobile.compliance_traffic_light',
    queryFn: fetchComplianceTrafficLight,
  });

  // Surface-access gate (42501). Only treat it as no-access when we have NO cached data to fall back to —
  // if the cache has a saved basket we keep showing it (the error is then just a failed refresh).
  const noAccess = errorCode(feed.error) === NO_ACCESS_CODE;

  // Never blank: prefer fresh/placeholder data; the synchronous cached envelope is already seeded by
  // useCachedQuery's placeholderData. Default to an empty list so a cold offline start still renders.
  const rows = feed.data ?? [];

  // "Showing saved data" affordance when serving last-known-good (offline or pre-fetch).
  const showStale = feed.isFromCache && feed.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && feed.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(feed.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // Sort worst-first (red → amber → green), then by open_count desc within a band.
  const sorted = [...rows].sort((a, b) => {
    const byRag = (RAG_ORDER[a.rag] ?? 3) - (RAG_ORDER[b.rag] ?? 3);
    if (byRag !== 0) return byRag;
    return (b.open_count ?? 0) - (a.open_count ?? 0);
  });

  const counts = sorted.reduce(
    (acc, r) => {
      if (r.rag === 'red') acc.red += 1;
      else if (r.rag === 'amber') acc.amber += 1;
      else if (r.rag === 'green') acc.green += 1;
      return acc;
    },
    { red: 0, amber: 0, green: 0 },
  );

  // No cached data AND access denied → graceful no-access state (don't blank).
  const showNoAccess = noAccess && rows.length === 0;
  // No cached data, no error, finished loading, empty payload → empty state.
  const showEmpty = !showNoAccess && rows.length === 0 && !feed.isLoading;

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <p className="text-sm text-muted-foreground">Compliance</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">Traffic lights</h1>
        {showStale && (
          <p
            className={cn(
              'mt-1 text-[11px] font-medium uppercase tracking-wide',
              online ? 'text-muted-foreground/70' : 'text-amber-600',
            )}
          >
            {staleLabel}
          </p>
        )}
      </header>

      {/* Summary header — counts of red / amber / green across the basket. */}
      {rows.length > 0 && (
        <div className="mb-4 flex items-center gap-2" role="group" aria-label="Compliance status summary">
          {(['red', 'amber', 'green'] as const).map((rag) => {
            const style = RAG_STYLES[rag];
            return (
              <div
                key={rag}
                className="flex flex-1 flex-col items-center rounded-xl border border-border bg-card px-2 py-3"
              >
                <span
                  className={cn('text-xl font-semibold tabular-nums', style.iconClass)}
                  aria-hidden="true"
                >
                  {counts[rag]}
                </span>
                <span className="mt-0.5 flex items-center gap-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <span className={cn('inline-block h-2 w-2 rounded-full', style.dot)} aria-hidden="true" />
                  {style.label}
                </span>
                <span className="sr-only">
                  {counts[rag]} portfolios {style.label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {showNoAccess && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-heading text-sm font-semibold">No access</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            You don&apos;t have access to compliance traffic lights. This view is available to CIO,
            portfolio-manager and analyst roles.
          </p>
        </div>
      )}

      {showEmpty && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-heading text-sm font-semibold">Nothing to show</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            No compliance status is available for your portfolios right now.
          </p>
        </div>
      )}

      {/* Per-portfolio RAG cards. */}
      <div className="space-y-3">
        {sorted.map((row) => {
          const style = RAG_STYLES[row.rag] ?? RAG_STYLES.green;
          const { Icon } = style;
          return (
            <Link
              key={row.portfolio}
              to={`/partner/m/compliance/${encodeURIComponent(row.portfolio)}`}
              className="block rounded-xl border border-border bg-card p-4 transition-colors active:bg-muted/50"
              aria-label={`${row.portfolio}: ${style.label} — ${ragSeverityLabel(row.rag)}, ${row.open_count} open ${
                row.open_count === 1 ? 'finding' : 'findings'
              }. View findings.`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  {/* RAG indicator: colour + dot + icon + text (never colour alone). */}
                  <span
                    className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', style.dot)}
                    aria-hidden="true"
                  />
                  <Icon className={cn('h-4 w-4 shrink-0', style.iconClass)} aria-hidden="true" />
                  <h2 className="truncate font-heading text-sm font-semibold">{row.portfolio}</h2>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                      style.badge,
                    )}
                  >
                    {style.label}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/50" aria-hidden="true" />
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  {row.open_count} open {row.open_count === 1 ? 'finding' : 'findings'}
                </p>
                {row.worst_severity && (
                  <span className="shrink-0 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                    Worst: {row.worst_severity}
                  </span>
                )}
              </div>

              {row.worst_title && (
                <p className="mt-1 truncate text-sm text-foreground" title={row.worst_title}>
                  {row.worst_title}
                </p>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
